import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import dynamic from "next/dynamic";
import styles from "./SharedSearchBar.module.scss";

const T1819SearchBar = dynamic(() => import("@components/T1819SearchBar"));

// useLayoutEffect á aðeins við í vafranum; á server notum við useEffect til að forðast SSR-aðvörun.
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

const SharedSearchBarContext = createContext(null);

export function useSharedSearchBar() {
    return useContext(SharedSearchBarContext);
}

/**
 * Heldur einum lifandi `T1819SearchBar` sem aldrei er fjarlægður/endurfluttur í DOM-trénu.
 * Reiturinn er staðsettur (position: fixed) yfir það "hólf" (SearchBarSlot) sem er virkt
 * hverju sinni. Þannig getur sami input-reiturinn birst bæði miðjaður á forsíðu og uppi í
 * haus án þess að fókus (og þar með farsímalyklaborðið) núllstillist við umbreytinguna.
 */
export function SharedSearchBarProvider({ children, searchBarProps = {} }) {
    // { el, mode } — hólfið sem reiturinn á að liggja yfir.
    const [slot, setSlot] = useState(null);
    const overlayRef = useRef(null);
    // Hvaða hólf var síðast virkt — notað til að ákveða hvort eigi að hreyfa (animate)
    // umbreytinguna. Við viljum aðeins hreyfingu þegar skipt er á milli hólfa, ekki við skrun.
    const prevModeRef = useRef(null);

    const registerSlot = useCallback((el, mode) => {
        setSlot({ el, mode });
        return () => {
            setSlot((current) => (current && current.el === el ? null : current));
        };
    }, []);

    useIsomorphicLayoutEffect(() => {
        const overlay = overlayRef.current;
        if (!overlay) {
            return undefined;
        }

        if (!slot || !slot.el) {
            overlay.style.opacity = "0";
            overlay.style.pointerEvents = "none";
            prevModeRef.current = null;
            return undefined;
        }

        const isHeader = slot.mode === "header";

        // Í haus-ham situr reiturinn ofan á hausnum (z-index > haus = 100) og er fastur
        // (fixed) — hausinn hreyfist ekki við skrun. Á forsíðu er reiturinn absolute í
        // skjals-hnitum svo hann skrolli náttúrulega með efninu (enginn JS við skrun) og
        // fari undir hausinn (z-index < 100).
        overlay.style.zIndex = isHeader ? "101" : "99";

        const position = () => {
            const rect = slot.el.getBoundingClientRect();
            overlay.style.opacity = "1";
            overlay.style.pointerEvents = "auto";
            overlay.style.width = `${rect.width}px`;

            if (isHeader) {
                // Fastur (fixed) og miðjaður LÓÐRÉTT yfir miðju hólfsins, svo reiturinn sitji
                // rétt í hausnum óháð því hvort hann sé hár eða lágur (ekki of hátt uppi).
                overlay.style.position = "fixed";
                overlay.style.transform = "translateY(-50%)";
                overlay.style.top = `${rect.top + rect.height / 2}px`;
                overlay.style.left = `${rect.left}px`;
            } else {
                // Absolute miðað við næsta staðsetta forföður (.page). Mismunurinn er
                // óháður skruni, svo reiturinn fylgir efninu án nokkurra scroll-viðburða.
                overlay.style.position = "absolute";
                overlay.style.transform = "none";
                const parent = overlay.offsetParent || document.body;
                const parentRect = parent.getBoundingClientRect();
                overlay.style.top = `${rect.top - parentRect.top}px`;
                overlay.style.left = `${rect.left - parentRect.left}px`;
            }
        };

        // Hreyfa aðeins þegar skipt er á milli ólíkra hólfa (t.d. forsíða -> haus),
        // ekki við fyrstu birtingu og aldrei við skrun.
        const shouldAnimate = prevModeRef.current && prevModeRef.current !== slot.mode;
        overlay.style.transition = shouldAnimate
            ? "top 0.25s ease, left 0.25s ease, width 0.25s ease, transform 0.25s ease"
            : "none";

        position();
        prevModeRef.current = slot.mode;

        // Endurmæla eftir að útlit haussins og hugsanlegt skrun-til-topps hefur sest,
        // svo reiturinn lendi örugglega á réttum stað (t.d. í hausnum eftir umbreytingu).
        const rafId = requestAnimationFrame(position);
        const repositionTimers = [
            setTimeout(position, 150),
            setTimeout(position, 400),
            setTimeout(position, 700),
        ];

        let animationTimeout;
        if (shouldAnimate) {
            animationTimeout = setTimeout(() => {
                overlay.style.transition = "none";
            }, 300);
        }

        // Aðeins endurstaðsetja við stærðarbreytingar (layout) — ALDREI við venjulegt skrun.
        const observer =
            typeof ResizeObserver !== "undefined" ? new ResizeObserver(position) : null;
        if (observer) {
            observer.observe(slot.el);
            observer.observe(document.documentElement);
        }
        window.addEventListener("resize", position);

        // iOS: þegar lyklaborðið opnast/sest breytist "visual viewport" og getBoundingClientRect
        // verður rétt fyrst þá. Endurstaðsetjum við þá viðburði svo reiturinn lendi rétt án þess
        // að notandinn þurfi að skruna. (visualViewport 'scroll' kemur EKKI við venjulegt skrun.)
        const vv = window.visualViewport;
        if (vv) {
            vv.addEventListener("resize", position);
            vv.addEventListener("scroll", position);
        }

        return () => {
            cancelAnimationFrame(rafId);
            repositionTimers.forEach(clearTimeout);
            if (animationTimeout) {
                clearTimeout(animationTimeout);
            }
            if (observer) {
                observer.disconnect();
            }
            window.removeEventListener("resize", position);
            if (vv) {
                vv.removeEventListener("resize", position);
                vv.removeEventListener("scroll", position);
            }
        };
    }, [slot]);

    const mode = slot?.mode || "front";

    // Stöðug auðkenning svo SearchBarSlot-effektinn keyri ekki í óendanlega lykkju.
    const contextValue = useMemo(() => ({ registerSlot }), [registerSlot]);

    return (
        <SharedSearchBarContext.Provider value={contextValue}>
            {children}
            <div ref={overlayRef} className={styles.overlay}>
                <T1819SearchBar small={mode === "header"} {...searchBarProps} />
            </div>
        </SharedSearchBarContext.Provider>
    );
}

/**
 * Ósýnilegt hólf sem skráir sig hjá provider-inum og skilgreinir hvar sameiginlegi
 * reiturinn á að liggja. Tekur pláss í venjulegu flæði svo umbreytingin sé eðlileg.
 */
export function SearchBarSlot({ mode = "front", className, style }) {
    const context = useSharedSearchBar();
    const ref = useRef(null);

    useIsomorphicLayoutEffect(() => {
        if (!context || !ref.current) {
            return undefined;
        }
        return context.registerSlot(ref.current, mode);
    }, [context, mode]);

    return <div ref={ref} aria-hidden className={className} style={style} />;
}

export default SharedSearchBarProvider;
